import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonTuples = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Immutable Records</div>
                <h1>Tuples: Packing & Unpacking</h1>
                <p className="hero-lead">
                    Tuples are not just "immutable lists". They are structurally different.
                    While lists are for collections of the same type, tuples are for records of different types with a fixed structure.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    A common interview question is: <em>"What is the difference between a list and a tuple?"</em>
                    The junior answer is: "Tuples are immutable."
                    The senior answer is: "Lists are for homogeneous sequences. Tuples are for heterogeneous structures."
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>The Philosophy:</strong> Why <code>(x, y)</code> is a tuple but <code>[x, y]</code> is a list.</li>
                        <li><strong>Internals:</strong> How Single-Block Allocation makes tuples faster and lighter.</li>
                        <li><strong>Unpacking:</strong> The art of <code>a, b = b, a</code> and star expressions <code>*rest</code>.</li>
                        <li><strong>NamedTuples:</strong> Creating readable records without full Classes.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Memory Internals: Single-Block Allocation</h2>
                <p>
                    Lists need two blocks of memory: the list object itself and the separate array of pointers (because it needs to resize).
                    Tuples, being immutable, are allocated as a <strong>single block</strong> of memory.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>List vs Tuple Memory Overhead</span>
                    </div>
                    <CodeBlock language="python" code={`import sys

l = [1, 2, 3]
t = (1, 2, 3)

# Lists carry "Growth Room" baggage
print(sys.getsizeof(l))  # ~88 bytes

# Tuples are perfectly sized
print(sys.getsizeof(t))  # ~64 bytes (Significant savings at scale!)`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Optimization:</strong> Because tuples are immutable, Python's runtime can cache small tuples, making creation instant compared to lists.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>The Art of Unpacking</h2>
                <p>
                    Tuple unpacking (or destructuring) is one of Python's most beautiful features. It allows you to assign multiple variables at once.
                </p>

                <h3>1. The Swap</h3>
                <p>No temp variables needed.</p>
                <CodeBlock language="python" code={`a = 10
b = 20
a, b = b, a  # Swapped!`} />

                <h3>2. The Star (Extended Unpacking)</h3>
                <p>Capture the "rest" of the items.</p>
                <CodeBlock language="python" code={`data = (1, 2, 3, 4, 5, 6)

first, *middle, last = data

print(first)   # 1
print(middle)  # [2, 3, 4, 5] (Note: The 'rest' becomes a LIST)
print(last)    # 6`} />

                <h3>3. Ignoring Values</h3>
                <p>Use <code>_</code> (underscore) for values you don't need.</p>
                <CodeBlock language="python" code={`person = ("Rohit", 30, "Engineer", "New York")
name, _, role, _ = person
# We only cared about name and role`} />
            </div>

            <div className="content-section">
                <h2>NamedTuples: Self-Documenting Code</h2>
                <p>
                    Standard tuples have a weakness: you access data by index <code>p[0]</code>, which is unreadable.
                    If you don't want to write a full Class, use <code>collections.namedtuple</code>.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>From Mystery to Clarity</span>
                    </div>
                    <CodeBlock language="python" code={`from collections import namedtuple

#  The Mystery Tuple
# What is 255? What is 0?
color = (255, 0, 0)

# The Named Tuple
Color = namedtuple("Color", ["red", "green", "blue"])
red = Color(255, 0, 0)

print(red.red)    # 255 (Access by name!)
print(red[0])     # 255 (Still works by index)
print(red)        # Color(red=255, green=0, blue=0) (Nice repr)`} />
                </div>
            </div>


            <div className="content-section best-practices">
                <h2>When to use Tuples?</h2>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Use Tuples When...</h3>
                        <ul>
                            <li>Data represents a fixed record (e.g., coordinates <code>x, y</code>).</li>
                            <li>You want to use the data as a <strong>Dictionary Key</strong> (Lists can't be keys!).</li>
                            <li>You are returning multiple values from a function.</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Use Lists When...</h3>
                        <ul>
                            <li>The data needs to grow or shrink.</li>
                            <li>The collection is homogeneous (e.g., a list of filenames).</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PythonTuples;

